import React, { FC, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { UserAuth } from '../../AuthContext';
import { useTDTier } from '../../hooks/useTDTier';
import TierGate from '../../components/TierGate/TierGate';
import { Box, Flex, Text, Heading, Spinner, Center } from '@chakra-ui/react';
import QRCode from 'qrcode';

const F = { ui:"'Rajdhani','Arial',sans-serif", mono:"'Share Tech Mono',monospace" };
const C = { card:'#131720',border:'#1E2330',gold:'#D4A017',teal:'#19E6CF',
  white:'#F0F4FF',ink:'#C8D0E0',dim:'#6B7590',green:'#22C55E',red:'#EF4444' };

const LICENSE_LABELS: Record<string,string> = {
  private_home_game:'Private Home Game', charitable:'Charitable / Fundraiser',
  pctds_sanctioned:'PCTDS Sanctioned', licensed_venue:'Licensed Venue',
  casino_night:'Casino Night', corporate:'Corporate Event', other:'',
};

const LAYOUTS = [
  { id:'portrait', label:'Print Poster', desc:'Letter/A4 · Print ready', w:816, h:1056, icon:'🖨' },
  { id:'square',   label:'Instagram Post', desc:'1080×1080 · Square', w:540, h:540, icon:'📸' },
  { id:'story',    label:'Story / Reel', desc:'1080×1920 · Vertical', w:405, h:720, icon:'📱' },
];

const PosterGeneratorPage: FC = () => {
  const { user } = UserAuth();
  const { canUsePosterGenerator, loading: tierLoading } = useTDTier();
  const navigate = useNavigate();
  const posterRef = useRef<HTMLDivElement>(null);

  const [events, setEvents] = useState<any[]>([]);
  const [venues, setVenues] = useState<any[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [selectedVenue, setSelectedVenue] = useState<any>(null);
  const [layout, setLayout] = useState<'portrait'|'square'|'story'>('portrait');
  const [loading, setLoading] = useState(true);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [printing, setPrinting] = useState(false);

  // Custom overrides
  const [customTitle, setCustomTitle] = useState('');
  const [customTagline, setCustomTagline] = useState('');
  const [showPrizePool, setShowPrizePool] = useState(true);
  const [showHouseRules, setShowHouseRules] = useState(true);
  const [showCharity, setShowCharity] = useState(true);
  const [showQR, setShowQR] = useState(true);

  // Design customization
  const [customPrimary, setCustomPrimary] = useState('');
  const [customSecondary, setCustomSecondary] = useState('');
  const [bgMode, setBgMode] = useState<'dark'|'light'|'photo'>('dark');
  const [bgPhotoUrl, setBgPhotoUrl] = useState('');
  const [customLogoUrl, setCustomLogoUrl] = useState('');
  const [sponsorLogoUrl, setSponsorLogoUrl] = useState('');
  const [fontStyle, setFontStyle] = useState<'rajdhani'|'mono'|'clean'>('rajdhani');
  const bgPhotoRef = React.useRef<HTMLInputElement>(null);
  const logoUploadRef = React.useRef<HTMLInputElement>(null);
  const sponsorUploadRef = React.useRef<HTMLInputElement>(null);

  const handleImageUpload = (file: File, setter: (url: string) => void) => {
    const reader = new FileReader();
    reader.onload = (e) => setter(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    const load = async () => {
      if (!user?.uid) return;
      try {
        const [evSnap, vSnap] = await Promise.all([
          getDocs(query(collection(db,'events'), where('admin_uid','==',user.uid),
            orderBy('start','desc'))),
          getDocs(query(collection(db,'venues'), where('td_uid','==',user.uid))),
        ]);
        const evs = evSnap.docs.map(d => ({ id:d.id, ...d.data() }));
        const vs = vSnap.docs.map(d => ({ id:d.id, ...d.data() }));
        setEvents(evs);
        setVenues(vs);
        if (evs.length > 0) {
          setSelectedEvent(evs[0]);
          setCustomTitle(evs[0].name ?? '');
        }
        if (vs.length > 0) setSelectedVenue(vs[0]);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [user?.uid]);

  useEffect(() => {
    if (!selectedEvent?.id) return;
    const url = `${window.location.origin}/live/${selectedEvent.id}`;
    QRCode.toDataURL(url, { width:200, margin:1,
      color:{ dark:'#000000', light:'#FFFFFF' } })
      .then(setQrDataUrl).catch(console.error);
  }, [selectedEvent?.id]);

  const primary = customPrimary || selectedVenue?.brand_color_primary || '#19E6CF';
  const secondary = customSecondary || selectedVenue?.brand_color_secondary || '#D4A017';
  const fontFamilyUI = fontStyle === 'clean' ? "'Arial',sans-serif" : fontStyle === 'mono' ? "'Share Tech Mono',monospace" : "'Rajdhani','Arial',sans-serif";
  const effectiveLogo = customLogoUrl || selectedVenue?.logo_url || '';
  const lConfig = LAYOUTS.find(l => l.id === layout)!;

  const formatDate = (iso: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-CA', { weekday:'long', month:'long', day:'numeric', year:'numeric' });
  };
  const formatTime = (iso: string) => {
    if (!iso) return '';
    return new Date(iso).toLocaleTimeString('en-CA', { hour:'numeric', minute:'2-digit' });
  };

  const handlePrint = () => {
    setPrinting(true);
    setTimeout(() => {
      window.print();
      setPrinting(false);
    }, 300);
  };

  if (loading) return <Center h="400px"><Spinner size="xl" color="teal.400" /></Center>;

  if (!user?.uid) return (
    <Center h="400px"><Text color="gray.400">Sign in to use the Poster Generator.</Text></Center>
  );

  // ── POSTER CONTENT ─────────────────────────────────────────────────────
  const isPortrait = layout === 'portrait';
  const isSquare = layout === 'square';
  const isStory = layout === 'story';

  const posterStyle: React.CSSProperties = {
    width: lConfig.w,
    height: lConfig.h,
    position: 'relative',
    overflow: 'hidden',
    background: '#080A0E',
    fontFamily: F.ui,
    flexShrink: 0,
  };

  const eventTitle = customTitle || selectedEvent?.name || 'Poker Night';
  const tagline = customTagline || selectedVenue?.schedule || '';
  const location = selectedEvent?.location?.address?.label || selectedVenue?.city || '';
  const buyIn = selectedEvent?.buy_in > 0 ? `$${selectedEvent.buy_in} Buy-in` : '';
  const gameType = selectedEvent?.game_type ?? 'Texas Hold\'em';
  const houseRules = selectedVenue?.house_rules ?? '';
  const licenseLabel = LICENSE_LABELS[selectedVenue?.license_type ?? ''] ?? '';

  const Poster = () => (
    <div style={posterStyle} ref={posterRef} id="poster-content">

      {/* Background */}
      {bgMode === 'photo' && bgPhotoUrl ? (
        <>
          <div style={{ position:'absolute', inset:0, backgroundImage:`url(${bgPhotoUrl})`,
            backgroundSize:'cover', backgroundPosition:'center' }} />
          <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.72)' }} />
        </>
      ) : bgMode === 'light' ? (
        <div style={{ position:'absolute', inset:0,
          background:`linear-gradient(135deg, #F8F9FA 0%, ${primary}12 50%, #F0F4FF 100%)` }} />
      ) : (
        <div style={{ position:'absolute', inset:0,
          background:`linear-gradient(135deg, #080A0E 0%, ${primary}18 50%, #080A0E 100%)` }} />
      )}

      {/* Light mode text color override */}
      <style>{bgMode === 'light' ? `
        .poster-text-primary { color: #111827 !important; }
        .poster-text-secondary { color: #374151 !important; }
        .poster-text-dim { color: #6B7280 !important; }
      ` : ''}</style>

      {/* Top accent line */}
      <div style={{ position:'absolute', top:0, left:0, right:0, height:6,
        background:`linear-gradient(90deg, ${primary}, ${secondary}, ${primary})` }} />

      {/* Bottom accent line */}
      <div style={{ position:'absolute', bottom:0, left:0, right:0, height:3,
        background:`linear-gradient(90deg, ${secondary}, ${primary})` }} />

      {/* Decorative corner suit */}
      <div style={{ position:'absolute', top:20, right:20,
        fontSize: isPortrait ? 80 : isSquare ? 60 : 50,
        color:`${primary}15`, fontWeight:900, lineHeight:1 }}>♠</div>
      <div style={{ position:'absolute', bottom:40, left:20,
        fontSize: isPortrait ? 60 : 40,
        color:`${secondary}12`, fontWeight:900, lineHeight:1 }}>♣</div>

      {/* Content */}
      <div style={{ position:'relative', zIndex:1, height:'100%',
        display:'flex', flexDirection:'column',
        padding: isPortrait ? '40px 48px' : isSquare ? '28px 32px' : '36px 28px' }}>

        {/* Header — logo + venue name */}
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom: isPortrait ? 32 : 20 }}>
          {/* Sponsor logo */}
          {sponsorLogoUrl && (
            <img src={sponsorLogoUrl} alt="sponsor"
              style={{ width:isPortrait?40:30, height:isPortrait?40:30,
                borderRadius:6, objectFit:'contain',
                border:`1px solid ${primary}44`, marginRight:4 }} />
          )}
          {effectiveLogo ? (
            <img src={effectiveLogo} alt="logo"
              style={{ width: isPortrait?52:38, height:isPortrait?52:38,
                borderRadius:8, objectFit:'cover',
                border:`2px solid ${primary}` }} />
          ) : (
            <div style={{ width:isPortrait?48:36, height:isPortrait?48:36,
              borderRadius:8, background:`${primary}22`,
              border:`2px solid ${primary}`,
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:isPortrait?24:18 }}>🃏</div>
          )}
          <div>
            <div style={{ fontFamily:F.mono, fontSize:isPortrait?11:9,
              color:primary, letterSpacing:'0.2em', textTransform:'uppercase' }}>
              {selectedVenue?.name ?? 'PokerCity'}
            </div>
            {selectedVenue?.pctds_status === 'verified' && (
              <div style={{ fontFamily:F.mono, fontSize:8,
                color:primary, letterSpacing:'0.15em' }}>✅ PCTDS VERIFIED</div>
            )}
          </div>
        </div>

        {/* Main title */}
        <div style={{ flex:1, display:'flex', flexDirection:'column', justifyContent:'center' }}>
          <div style={{ fontFamily:F.mono, fontSize:isPortrait?11:9,
            color:secondary, letterSpacing:'0.25em', marginBottom:8,
            textTransform:'uppercase' }}>
            YOU ARE INVITED
          </div>

          <h1 style={{ fontFamily:fontFamilyUI, fontSize:isPortrait?52:isSquare?38:34,
            fontWeight:900, color:'#FFFFFF', lineHeight:1.1,
            margin:'0 0 12px', textTransform:'uppercase',
            letterSpacing:'-0.02em' }}>
            {eventTitle}
          </h1>

          {tagline && (
            <p style={{ fontFamily:F.ui, fontSize:isPortrait?18:14,
              color:`${primary}CC`, margin:'0 0 24px', fontWeight:600 }}>
              {tagline}
            </p>
          )}

          {/* Date + time */}
          {selectedEvent?.start && (
            <div style={{ marginBottom:20 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                <span style={{ color:secondary, fontSize:isPortrait?16:13 }}>📅</span>
                <span style={{ fontFamily:F.ui, fontSize:isPortrait?20:16,
                  fontWeight:700, color:'#F0F4FF' }}>
                  {formatDate(selectedEvent.start)}
                </span>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ color:secondary, fontSize:isPortrait?16:13 }}>🕐</span>
                <span style={{ fontFamily:F.ui, fontSize:isPortrait?20:16,
                  fontWeight:700, color:'#F0F4FF' }}>
                  {formatTime(selectedEvent.start)}
                </span>
              </div>
            </div>
          )}

          {/* Location */}
          {location && (
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:16 }}>
              <span style={{ fontSize:isPortrait?16:13 }}>📍</span>
              <span style={{ fontFamily:F.ui, fontSize:isPortrait?16:13,
                color:'#C8D0E0', fontWeight:600 }}>{location}</span>
            </div>
          )}

          {/* Game details chips */}
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:20 }}>
            {gameType && (
              <span style={{ fontFamily:F.mono, fontSize:isPortrait?11:9,
                color:primary, background:`${primary}18`,
                border:`1px solid ${primary}44`,
                borderRadius:4, padding:'4px 10px',
                letterSpacing:'0.1em', textTransform:'uppercase' }}>
                {gameType}
              </span>
            )}
            {buyIn && (
              <span style={{ fontFamily:F.mono, fontSize:isPortrait?11:9,
                color:secondary, background:`${secondary}18`,
                border:`1px solid ${secondary}44`,
                borderRadius:4, padding:'4px 10px',
                letterSpacing:'0.1em' }}>
                {buyIn}
              </span>
            )}
            {licenseLabel && (
              <span style={{ fontFamily:F.mono, fontSize:isPortrait?10:8,
                color:'#6B7590', background:'#131720',
                border:'1px solid #1E2330',
                borderRadius:4, padding:'4px 10px',
                letterSpacing:'0.08em' }}>
                {licenseLabel}
              </span>
            )}
          </div>

          {/* House rules — portrait only */}
          {isPortrait && showHouseRules && houseRules && (
            <div style={{ background:`${secondary}12`,
              border:`1px solid ${secondary}44`,
              borderRadius:8, padding:'12px 16px', marginBottom:16 }}>
              <div style={{ fontFamily:F.mono, fontSize:10,
                color:secondary, letterSpacing:'0.15em', marginBottom:6 }}>
                HOUSE RULES
              </div>
              {houseRules.split('\n').map((r:string, i:number) => r.trim() && (
                <div key={i} style={{ fontFamily:F.ui, fontSize:12,
                  color:'#C8D0E0', lineHeight:1.6 }}>• {r.trim()}</div>
              ))}
            </div>
          )}
        </div>

        {/* Footer — QR + pokercity */}
        <div style={{ display:'flex', alignItems:'flex-end',
          justifyContent:'space-between', marginTop:'auto', paddingTop:16,
          borderTop:`1px solid ${primary}33` }}>
          <div>
            {showQR && qrDataUrl && (
              <div>
                <img src={qrDataUrl} alt="QR"
                  style={{ width:isPortrait?80:60, height:isPortrait?80:60,
                    borderRadius:4, border:`2px solid ${primary}` }} />
                <div style={{ fontFamily:F.mono, fontSize:8,
                  color:'#6B7590', letterSpacing:'0.1em', marginTop:4 }}>
                  SCAN FOR LIVE INFO
                </div>
              </div>
            )}
          </div>
          <div style={{ textAlign:'right' }}>
            <div style={{ display:'flex', alignItems:'center', gap:6,
              justifyContent:'flex-end', marginBottom:2 }}>
              <img src="/favicon.gif" alt="PokerCity"
                style={{ width:10, height:18, objectFit:'contain' }} />
              <span style={{ fontFamily:F.mono, fontSize:9,
                color:'#3A4050', letterSpacing:'0.12em' }}>
                POWERED BY POKERCITY.COM
              </span>
            </div>
            <div style={{ fontFamily:F.mono, fontSize:8,
              color:'#2A3040', letterSpacing:'0.08em' }}>
              pokercity.com/live/{selectedEvent?.id?.substring(0,8)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Tier gate — must be after all hooks
  if (!tierLoading && !canUsePosterGenerator) return (
    <TierGate allowed={false} feature="Poster Generator" requiredTier="pro"><div /></TierGate>
  );

  return (
    <Box maxW="1200px" mx="auto" px={4} py={8} fontFamily={F.ui}>

      {/* Print styles */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #poster-content, #poster-content * { visibility: visible !important; }
          #poster-content {
            position: fixed !important; top: 0 !important; left: 0 !important;
            width: 100vw !important; height: 100vh !important;
            transform: none !important;
          }
        }
      `}</style>

      <Heading style={{ fontFamily:F.ui, color:C.white, fontSize:28, marginBottom:6 }}>
        🎨 Poster Generator
      </Heading>
      <Text style={{ fontFamily:F.ui, fontSize:13, color:C.dim, marginBottom:24 }}>
        Generate branded event posters for print, Instagram, or Stories.
        Brand colors and venue info are pulled from your venue profile automatically.
      </Text>

      <Flex gap={6} align="flex-start" flexWrap="wrap">

        {/* ── LEFT: Controls ── */}
        <Box flex={1} minW="280px" maxW="340px">

          {/* Event selector */}
          <Box bg={C.card} border={`1px solid ${C.border}`} borderRadius={10}
            overflow="hidden" mb={4}>
            <Box bg="#0D1520" px={4} py={3} borderBottom={`1px solid ${C.border}`}>
              <Text style={{ fontFamily:F.mono, fontSize:11, color:C.teal, letterSpacing:'0.2em' }}>
                SELECT EVENT
              </Text>
            </Box>
            <Box p={4}>
              <select
                style={{ display:'block',width:'100%',padding:'10px 12px',
                  background:'#0D1118',border:`1px solid ${C.border}`,
                  borderRadius:6,color:C.white,fontSize:14,fontFamily:F.ui,cursor:'pointer' }}
                value={selectedEvent?.id ?? ''}
                onChange={e => {
                  const ev = events.find(ev => ev.id === e.target.value);
                  setSelectedEvent(ev);
                  setCustomTitle(ev?.name ?? '');
                }}>
                <option value="">Select an event...</option>
                {events.map(ev => (
                  <option key={ev.id} value={ev.id}>
                    {ev.name ?? 'Untitled'} {ev.start ? `· ${new Date(ev.start).toLocaleDateString('en-CA',{month:'short',day:'numeric'})}` : ''}
                  </option>
                ))}
              </select>
            </Box>
          </Box>

          {/* Venue selector */}
          {venues.length > 1 && (
            <Box bg={C.card} border={`1px solid ${C.border}`} borderRadius={10}
              overflow="hidden" mb={4}>
              <Box bg="#0D1520" px={4} py={3} borderBottom={`1px solid ${C.border}`}>
                <Text style={{ fontFamily:F.mono, fontSize:11, color:C.teal, letterSpacing:'0.2em' }}>
                  VENUE BRANDING
                </Text>
              </Box>
              <Box p={4}>
                <select
                  style={{ display:'block',width:'100%',padding:'10px 12px',
                    background:'#0D1118',border:`1px solid ${C.border}`,
                    borderRadius:6,color:C.white,fontSize:14,fontFamily:F.ui,cursor:'pointer' }}
                  value={selectedVenue?.id ?? ''}
                  onChange={e => setSelectedVenue(venues.find(v => v.id === e.target.value))}>
                  {venues.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </Box>
            </Box>
          )}

          {/* Layout picker */}
          <Box bg={C.card} border={`1px solid ${C.border}`} borderRadius={10}
            overflow="hidden" mb={4}>
            <Box bg="#0D1520" px={4} py={3} borderBottom={`1px solid ${C.border}`}>
              <Text style={{ fontFamily:F.mono, fontSize:11, color:C.teal, letterSpacing:'0.2em' }}>
                FORMAT
              </Text>
            </Box>
            <Box p={3}>
              <Flex direction="column" gap={2}>
                {LAYOUTS.map(l => (
                  <Flex key={l.id} align="center" gap={3} p={3} borderRadius={8}
                    cursor="pointer"
                    bg={layout === l.id ? '#0D1F1E' : 'transparent'}
                    border={`1px solid ${layout === l.id ? C.teal : C.border}`}
                    onClick={() => setLayout(l.id as any)}>
                    <Text style={{ fontSize:20 }}>{l.icon}</Text>
                    <Box>
                      <Text style={{ fontFamily:F.ui, fontSize:14, fontWeight:700,
                        color: layout === l.id ? C.teal : C.white }}>{l.label}</Text>
                      <Text style={{ fontFamily:F.mono, fontSize:10, color:C.dim }}>
                        {l.desc}
                      </Text>
                    </Box>
                    {layout === l.id && (
                      <Text style={{ marginLeft:'auto', color:C.teal, fontWeight:700 }}>✓</Text>
                    )}
                  </Flex>
                ))}
              </Flex>
            </Box>
          </Box>

          {/* ── DESIGN SECTION ── */}
          <Box bg={C.card} border={`1px solid ${C.border}`} borderRadius={10} overflow="hidden" mb={4}>
            <Box bg="#0D1520" px={4} py={3} borderBottom={`1px solid ${C.border}`}>
              <Text style={{ fontFamily:F.mono, fontSize:11, color:C.teal, letterSpacing:'0.2em' }}>DESIGN</Text>
            </Box>
            <Box p={4}>
              <Flex direction="column" gap={4}>

                {/* Background mode */}
                <Box>
                  <Text style={{ fontFamily:F.mono, fontSize:10, color:C.dim, letterSpacing:'0.15em', marginBottom:8 }}>BACKGROUND</Text>
                  <Flex gap={2}>
                    {(['dark','light','photo'] as const).map(mode => (
                      <Box key={mode} as="button" flex={1} py={2}
                        bg={bgMode === mode ? '#0D1F1E' : 'transparent'}
                        border={`1px solid ${bgMode === mode ? C.teal : C.border}`}
                        borderRadius={6} fontFamily={F.mono} fontSize="10px"
                        color={bgMode === mode ? C.teal : C.dim}
                        cursor="pointer" letterSpacing="0.1em"
                        onClick={() => setBgMode(mode)}>
                        {mode === 'dark' ? '🌑 DARK' : mode === 'light' ? '☀️ LIGHT' : '🖼 PHOTO'}
                      </Box>
                    ))}
                  </Flex>
                  {bgMode === 'photo' && (
                    <Box mt={2}>
                      <input type="file" ref={bgPhotoRef} accept="image/*" style={{ display:'none' }}
                        onChange={e => e.target.files?.[0] && handleImageUpload(e.target.files[0], setBgPhotoUrl)} />
                      <Box as="button" w="100%" py={2}
                        bg="transparent" border={`1px dashed ${C.border}`}
                        borderRadius={6} fontFamily={F.ui} fontSize="12px"
                        color={bgPhotoUrl ? C.green : C.dim} cursor="pointer"
                        onClick={() => bgPhotoRef.current?.click()}>
                        {bgPhotoUrl ? '✓ Photo uploaded — click to change' : '+ Upload background photo'}
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* Color pickers */}
                <Box>
                  <Text style={{ fontFamily:F.mono, fontSize:10, color:C.dim, letterSpacing:'0.15em', marginBottom:8 }}>COLORS</Text>
                  <Flex gap={3} align="center" mb={2}>
                    <Box>
                      <Text style={{ fontFamily:F.mono, fontSize:9, color:C.dim, marginBottom:4 }}>PRIMARY</Text>
                      <Flex align="center" gap={2}>
                        <input type="color" value={primary}
                          onChange={e => setCustomPrimary(e.target.value)}
                          style={{ width:36, height:36, padding:2, borderRadius:6,
                            border:`1px solid ${C.border}`, background:'transparent', cursor:'pointer' }} />
                        <Text style={{ fontFamily:F.mono, fontSize:11, color:C.dim }}>{primary}</Text>
                      </Flex>
                    </Box>
                    <Box>
                      <Text style={{ fontFamily:F.mono, fontSize:9, color:C.dim, marginBottom:4 }}>SECONDARY</Text>
                      <Flex align="center" gap={2}>
                        <input type="color" value={secondary}
                          onChange={e => setCustomSecondary(e.target.value)}
                          style={{ width:36, height:36, padding:2, borderRadius:6,
                            border:`1px solid ${C.border}`, background:'transparent', cursor:'pointer' }} />
                        <Text style={{ fontFamily:F.mono, fontSize:11, color:C.dim }}>{secondary}</Text>
                      </Flex>
                    </Box>
                    <Box as="button" mt={4} px={3} py={1}
                      bg="transparent" border={`1px solid ${C.border}`}
                      borderRadius={6} fontFamily={F.mono} fontSize="9px"
                      color={C.dim} cursor="pointer"
                      onClick={() => { setCustomPrimary(''); setCustomSecondary(''); }}>
                      RESET
                    </Box>
                  </Flex>
                  {selectedVenue && !customPrimary && (
                    <Text style={{ fontFamily:F.ui, fontSize:11, color:C.dim }}>
                      Using {selectedVenue.name} brand colors
                    </Text>
                  )}
                </Box>

                {/* Font */}
                <Box>
                  <Text style={{ fontFamily:F.mono, fontSize:10, color:C.dim, letterSpacing:'0.15em', marginBottom:8 }}>FONT STYLE</Text>
                  <Flex gap={2}>
                    {[{id:'rajdhani',label:'IMPACT'},{id:'clean',label:'CLEAN'},{id:'mono',label:'MONO'}].map(f => (
                      <Box key={f.id} as="button" flex={1} py={2}
                        bg={fontStyle === f.id ? '#0D1F1E' : 'transparent'}
                        border={`1px solid ${fontStyle === f.id ? C.teal : C.border}`}
                        borderRadius={6} fontFamily={F.mono} fontSize="10px"
                        color={fontStyle === f.id ? C.teal : C.dim}
                        cursor="pointer" letterSpacing="0.1em"
                        onClick={() => setFontStyle(f.id as any)}>
                        {f.label}
                      </Box>
                    ))}
                  </Flex>
                </Box>

              </Flex>
            </Box>
          </Box>

          {/* ── BRANDING SECTION ── */}
          <Box bg={C.card} border={`1px solid ${C.border}`} borderRadius={10} overflow="hidden" mb={4}>
            <Box bg="#0D1520" px={4} py={3} borderBottom={`1px solid ${C.border}`}>
              <Text style={{ fontFamily:F.mono, fontSize:11, color:C.teal, letterSpacing:'0.2em' }}>BRANDING</Text>
            </Box>
            <Box p={4}>
              <Flex direction="column" gap={3}>

                {/* Logo upload */}
                <Box>
                  <Text style={{ fontFamily:F.mono, fontSize:10, color:C.dim, letterSpacing:'0.15em', marginBottom:6 }}>LOGO</Text>
                  <input type="file" ref={logoUploadRef} accept="image/*" style={{ display:'none' }}
                    onChange={e => e.target.files?.[0] && handleImageUpload(e.target.files[0], setCustomLogoUrl)} />
                  <Flex gap={2} align="center">
                    {(customLogoUrl || selectedVenue?.logo_url) && (
                      <img src={customLogoUrl || selectedVenue?.logo_url} alt="logo"
                        style={{ width:40, height:40, borderRadius:6, objectFit:'cover',
                          border:`1px solid ${C.border}` }} />
                    )}
                    <Box as="button" flex={1} py={2}
                      bg="transparent" border={`1px dashed ${C.border}`}
                      borderRadius={6} fontFamily={F.ui} fontSize="12px"
                      color={customLogoUrl ? C.green : C.dim} cursor="pointer"
                      onClick={() => logoUploadRef.current?.click()}>
                      {customLogoUrl ? '✓ Custom logo — click to change' : selectedVenue?.logo_url ? 'Using venue logo — override?' : '+ Upload logo'}
                    </Box>
                    {customLogoUrl && (
                      <Box as="button" px={2} py={2}
                        bg="transparent" border={`1px solid ${C.border}`}
                        borderRadius={6} fontFamily={F.mono} fontSize="10px"
                        color={C.dim} cursor="pointer"
                        onClick={() => setCustomLogoUrl('')}>✕</Box>
                    )}
                  </Flex>
                </Box>

                {/* Sponsor logo */}
                <Box>
                  <Text style={{ fontFamily:F.mono, fontSize:10, color:C.dim, letterSpacing:'0.15em', marginBottom:6 }}>SPONSOR LOGO</Text>
                  <input type="file" ref={sponsorUploadRef} accept="image/*" style={{ display:'none' }}
                    onChange={e => e.target.files?.[0] && handleImageUpload(e.target.files[0], setSponsorLogoUrl)} />
                  <Flex gap={2} align="center">
                    {sponsorLogoUrl && (
                      <img src={sponsorLogoUrl} alt="sponsor"
                        style={{ width:40, height:40, borderRadius:6, objectFit:'contain',
                          border:`1px solid ${C.border}` }} />
                    )}
                    <Box as="button" flex={1} py={2}
                      bg="transparent" border={`1px dashed ${C.border}`}
                      borderRadius={6} fontFamily={F.ui} fontSize="12px"
                      color={sponsorLogoUrl ? C.green : C.dim} cursor="pointer"
                      onClick={() => sponsorUploadRef.current?.click()}>
                      {sponsorLogoUrl ? '✓ Sponsor logo — click to change' : '+ Add sponsor logo (optional)'}
                    </Box>
                    {sponsorLogoUrl && (
                      <Box as="button" px={2} py={2}
                        bg="transparent" border={`1px solid ${C.border}`}
                        borderRadius={6} fontFamily={F.mono} fontSize="10px"
                        color={C.dim} cursor="pointer"
                        onClick={() => setSponsorLogoUrl('')}>✕</Box>
                    )}
                  </Flex>
                </Box>

              </Flex>
            </Box>
          </Box>

          {/* ── CONTENT SECTION ── */}
          <Box bg={C.card} border={`1px solid ${C.border}`} borderRadius={10} overflow="hidden" mb={4}>
            <Box bg="#0D1520" px={4} py={3} borderBottom={`1px solid ${C.border}`}>
              <Text style={{ fontFamily:F.mono, fontSize:11, color:C.teal, letterSpacing:'0.2em' }}>CONTENT</Text>
            </Box>
            <Box p={4}>
              <Flex direction="column" gap={3}>
                <Box>
                  <Text style={{ fontFamily:F.mono, fontSize:10, color:C.dim, letterSpacing:'0.15em', marginBottom:4 }}>TITLE OVERRIDE</Text>
                  <input value={customTitle}
                    onChange={e => setCustomTitle(e.target.value)}
                    placeholder="Leave blank to use event name"
                    style={{ display:'block',width:'100%',padding:'8px 12px',
                      background:'#0D1118',border:`1px solid ${C.border}`,
                      borderRadius:6,color:C.white,fontSize:13,fontFamily:F.ui,
                      boxSizing:'border-box' as any }} />
                </Box>
                <Box>
                  <Text style={{ fontFamily:F.mono, fontSize:10, color:C.dim, letterSpacing:'0.15em', marginBottom:4 }}>TAGLINE</Text>
                  <input value={customTagline}
                    onChange={e => setCustomTagline(e.target.value)}
                    placeholder={selectedVenue?.schedule ?? 'e.g. Every Thursday 7PM'}
                    style={{ display:'block',width:'100%',padding:'8px 12px',
                      background:'#0D1118',border:`1px solid ${C.border}`,
                      borderRadius:6,color:C.white,fontSize:13,fontFamily:F.ui,
                      boxSizing:'border-box' as any }} />
                </Box>

                {/* Toggle sections */}
                {[
                  { label:'Show house rules (portrait)', state:showHouseRules, setter:setShowHouseRules },
                  { label:'Show charity section', state:showCharity, setter:setShowCharity },
                  { label:'Show QR code', state:showQR, setter:setShowQR },
                ].map(item => (
                  <Flex key={item.label} align="center" gap={3} cursor="pointer"
                    onClick={() => item.setter((s:boolean) => !s)}>
                    <Box w="20px" h="20px" borderRadius={4}
                      bg={item.state ? C.teal : 'transparent'}
                      border={`2px solid ${item.state ? C.teal : C.border}`}
                      display="flex" alignItems="center" justifyContent="center">
                      {item.state && <Text style={{ fontSize:12,color:'#000',fontWeight:700 }}>✓</Text>}
                    </Box>
                    <Text style={{ fontFamily:F.ui, fontSize:13, color:C.ink }}>{item.label}</Text>
                  </Flex>
                ))}
              </Flex>
            </Box>
          </Box>

          {/* Print/Download button */}
          <Box as="button" w="100%" px={6} py={4}
            bg={printing ? C.card : '#1A0F00'}
            border={`1px solid ${printing ? C.border : C.gold}`}
            borderRadius={8} fontSize="15px" fontWeight="700"
            color={printing ? C.dim : C.gold}
            fontFamily={F.ui} cursor={printing ? 'wait' : 'pointer'}
            onClick={handlePrint} disabled={printing || !selectedEvent}>
            {printing ? 'Opening print dialog...' : '🖨 Print / Save as PDF →'}
          </Box>
          <Text style={{ fontFamily:F.ui, fontSize:11, color:C.dim,
            textAlign:'center', marginTop:8 }}>
            Use your browser's print dialog to save as PDF.
            Set margins to None for best results.
          </Text>
        </Box>

        {/* ── RIGHT: Poster preview ── */}
        <Box flex={2} minW="300px">
          <Text style={{ fontFamily:F.mono, fontSize:11, color:C.dim,
            letterSpacing:'0.2em', marginBottom:12 }}>PREVIEW</Text>
          <Box overflow="auto" bg="#0A0A0A" borderRadius={12} p={4}
            border={`1px solid ${C.border}`}
            style={{ display:'flex', justifyContent:'center', alignItems:'flex-start' }}>
            {selectedEvent ? (
              <div style={{ transform:`scale(${Math.min(1, 560/lConfig.w)})`,
                transformOrigin:'top center' }}>
                <Poster />
              </div>
            ) : (
              <Center h="400px" w="100%">
                <Box textAlign="center">
                  <Text style={{ fontSize:48, marginBottom:12 }}>🎨</Text>
                  <Text style={{ fontFamily:F.ui, fontSize:16, color:C.dim }}>
                    Select an event to preview your poster
                  </Text>
                </Box>
              </Center>
            )}
          </Box>
        </Box>
      </Flex>
    </Box>
  );
};

export default PosterGeneratorPage;
